import { useState } from 'react';

// Invite-only signup. Timezone is captured silently from the browser (Intl) so the weekly
// boundary / Sunday reflection logic is anchored to the user's own day from day one — no
// extra field for the user to fill. On success we reuse the same auth-restore callback the
// Login page uses; the new account lands on onboarding (onboarded=0).
export default function Signup({ onAuthed, onShowLogin }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    let timezone = 'UTC';
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* keep UTC */ }
    try {
      const r = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, inviteCode, timezone }),
      });
      if (r.ok) {
        onAuthed();
      } else {
        const data = await r.json().catch(() => ({}));
        setError(data.error || 'Could not create your account.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)',
    }}>
      <div className="fade-in" style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-lg)',
        padding: '48px 40px',
        width: 360,
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--border)',
      }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 32,
          marginBottom: 6,
        }}>Bearing360</h1>
        <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 36, fontWeight: 300 }}>
          Create your account
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            style={inputStyle}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Password (min 10 characters)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="Invite code"
            value={inviteCode}
            onChange={e => setInviteCode(e.target.value)}
            style={inputStyle}
          />
          {error && (
            <p style={{ fontSize: 13, color: 'var(--danger)', textAlign: 'center' }}>{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 8,
              padding: '12px',
              background: 'var(--text)',
              color: '#fff',
              borderRadius: 'var(--radius-sm)',
              fontSize: 14,
              fontWeight: 500,
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.15s',
            }}
          >{loading ? 'Creating…' : 'Create account'}</button>
        </form>

        {onShowLogin && (
          <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', marginTop: 22 }}>
            Already have an account?{' '}
            <button
              type="button"
              onClick={onShowLogin}
              style={{
                background: 'none', padding: 0, color: 'var(--text)',
                fontSize: 13, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer',
              }}
            >Sign in</button>
          </p>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '11px 14px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
  background: 'var(--bg)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
};
